/**
 * Pick an image, upload it, hand back the asset id.
 *
 * One component for every image the product accepts — a profile photograph now,
 * course covers and marketplace listing photos later. The upload flow itself is
 * `useUpload` (see `shared/api/uploads.ts`); this is the control around it.
 *
 * **Shared rather than per-screen.** The original reason was that the flow had
 * three steps with a failure mode at each, and a second copy was a second
 * chance to skip the confirm call where SEC-08's magic-byte check lived. Since
 * 20 October 2026 the upload is a single request and that particular hazard is
 * gone — but one control still means one place for the size check, the error
 * mapping, the preview and the accessible label, which every screen taking a
 * photograph needs identically.
 */
import { ImageUp, Loader2, Trash2 } from 'lucide-react'
import { useId, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { UploadFailed, useUpload } from '@/shared/api/uploads'

import { Button } from './Button'
import { cn } from './cn'
import { messageFor } from './form'

export type ImageUploadProps = {
  /** Current image, if any — a URL the server already resolved. */
  value?: string | null | undefined
  /** Called with the new asset id, or `null` when the image is removed. */
  onChange: (assetId: string | null) => void
  /** Rendered round for avatars, square for covers. */
  shape?: 'circle' | 'square'
  /** Describes what is being uploaded, for the file input's label. */
  label: string
  disabled?: boolean | undefined
  className?: string | undefined
}

export function ImageUpload({
  value,
  onChange,
  shape = 'circle',
  label,
  disabled,
  className,
}: ImageUploadProps) {
  const { t } = useTranslation('common')
  const upload = useUpload()
  const inputId = useId()
  const [error, setError] = useState<string | null>(null)
  /**
   * A local preview so the new image appears immediately.
   *
   * Without it the picture only changes after the form is saved and `/me/`
   * refetches — so a user picks a photograph, sees the old one, and picks
   * again.
   */
  const [preview, setPreview] = useState<string | null>(null)

  const shown = preview ?? value ?? null

  async function onPick(event: React.ChangeEvent<HTMLInputElement>) {
    const file = event.target.files?.[0]
    // Reset immediately: without this, picking the *same* file after a failure
    // fires no change event and the retry silently does nothing.
    event.target.value = ''
    if (!file) return

    setError(null)
    try {
      const asset = await upload.mutateAsync({ file, kind: 'image' })
      setPreview(asset.url ?? null)
      onChange(asset.id)
    } catch (cause) {
      // `file-too-large` never reaches the network, so it has no server message
      // and keeps the local string. Anything else is the API's refusal — the
      // declared type, the size cap or SEC-08's magic bytes — and it names
      // which, already translated. `t('upload.failed')` is the floor for a
      // response that carried no message at all, such as nginx's 502.
      setError(
        cause instanceof UploadFailed && cause.message === 'file-too-large'
          ? t('upload.tooLarge')
          : messageFor(cause, t('upload.failed')),
      )
    }
  }

  function onRemove() {
    setPreview(null)
    setError(null)
    onChange(null)
  }

  return (
    <div className={cn('space-y-2', className)}>
      <div className="flex items-center gap-4">
        <div
          className={cn(
            'grid size-20 shrink-0 place-items-center overflow-hidden border border-line bg-sunken',
            shape === 'circle' ? 'rounded-full' : 'rounded-lg',
          )}
        >
          {upload.isPending ? (
            <Loader2 aria-hidden="true" className="size-5 animate-spin text-fg-subtle" />
          ) : shown ? (
            // `alt=""` — the field's own label already names this, and the
            // photograph carries no information a reader needs described.
            <img src={shown} alt="" className="size-full object-cover" />
          ) : (
            <ImageUp aria-hidden="true" className="size-5 text-fg-subtle" />
          )}
        </div>

        <div className="flex flex-wrap items-center gap-2">
          {/* A real file input, visually hidden rather than `display: none`:
              hiding it outright removes it from the accessibility tree, and the
              label below is what makes it operable by keyboard. */}
          <input
            id={inputId}
            type="file"
            accept="image/*"
            className="sr-only"
            disabled={disabled || upload.isPending}
            onChange={(event) => void onPick(event)}
          />
          {/* A real `<label>` bound to the input, styled as a button.
              A `<button>` that calls `.click()` leaves the file input
              unlabelled — assistive technology announces "file upload" with no
              indication of what for, and the test could not find it either. */}
          <label
            htmlFor={inputId}
            className={cn(
              'inline-flex cursor-pointer items-center justify-center gap-2 rounded-lg border border-line-strong bg-surface px-3 py-1.5 text-sm font-medium text-fg transition-colors select-none hover:bg-sunken',
              (disabled || upload.isPending) && 'pointer-events-none opacity-55',
            )}
          >
            {shown ? t('upload.replace') : label}
          </label>

          {shown ? (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              disabled={disabled || upload.isPending}
              onClick={onRemove}
            >
              <Trash2 aria-hidden="true" />
              {t('upload.remove')}
            </Button>
          ) : null}
        </div>
      </div>

      {error ? (
        <p role="alert" className="text-sm text-danger">
          {error}
        </p>
      ) : null}
    </div>
  )
}
